import React, { useState, useEffect } from 'react';
import { Home, Calculator } from 'lucide-react';
import { SEO } from '../components/SEO';

interface ConstructionState {
  floorArea: string;
  floors: string;
  constructionType: 'standard' | 'premium' | 'luxury' | 'lowcost';
  includeBasement: boolean;
  includeGarage: boolean;
  includeRoof: boolean;
  includeInterior: boolean;
  includeUtilities: boolean;
  location: 'urban' | 'suburban' | 'rural';
}

interface ConstructionResult {
  totalArea: number;
  baseCost: number;
  basementCost: number;
  garageCost: number;
  roofCost: number;
  interiorCost: number;
  utilitiesCost: number;
  locationFactor: number;
  totalCost: number;
  costPerSquareMeter: number;
  estimatedTime: number;
}

// Base construction costs per m² in EUR
const constructionCosts = {
  'standard': 800,
  'premium': 1200,
  'luxury': 1800,
  'lowcost': 600
};

// Location factors
const locationFactors = {
  'urban': 1.2,
  'suburban': 1.0,
  'rural': 0.9
};

function KalkulatorGradnjeKuce() {
  const [values, setValues] = useState<ConstructionState>({
    floorArea: '',
    floors: '1',
    constructionType: 'standard',
    includeBasement: false,
    includeGarage: false,
    includeRoof: true,
    includeInterior: true,
    includeUtilities: true,
    location: 'suburban'
  });

  const [results, setResults] = useState<ConstructionResult | null>(null);

  const calculateConstruction = () => {
    const floorArea = parseFloat(values.floorArea);
    const floors = parseInt(values.floors);
    
    if (isNaN(floorArea) || isNaN(floors) || floorArea <= 0 || floors <= 0) {
      return null;
    }

    // Calculate total area
    const totalArea = floorArea * floors;
    
    // Calculate base construction cost
    const baseCost = totalArea * constructionCosts[values.constructionType];
    
    // Calculate additional costs
    const basementCost = values.includeBasement ? floorArea * 400 : 0;
    const garageCost = values.includeGarage ? 30 * 800 : 0; // Assuming 30m² garage
    const roofCost = values.includeRoof ? floorArea * 150 : 0;
    const interiorCost = values.includeInterior ? totalArea * 300 : 0;
    const utilitiesCost = values.includeUtilities ? totalArea * 100 : 0;
    
    // Apply location factor
    const locationFactor = locationFactors[values.location];
    
    // Calculate total cost
    const totalCost = (baseCost + basementCost + garageCost + roofCost + interiorCost + utilitiesCost) * locationFactor;
    
    // Calculate cost per square meter
    const costPerSquareMeter = totalCost / totalArea;
    
    // Estimate construction time in months
    const estimatedTime = Math.max(6, Math.ceil(totalArea / 50) + (values.includeBasement ? 2 : 0));
    
    return {
      totalArea,
      baseCost,
      basementCost,
      garageCost,
      roofCost,
      interiorCost,
      utilitiesCost,
      locationFactor,
      totalCost,
      costPerSquareMeter,
      estimatedTime
    };
  };

  const handleInputChange = (field: keyof ConstructionState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.floorArea && values.floors) {
      const results = calculateConstruction();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const constructionTypeNames = {
    'standard': 'Standardna gradnja',
    'premium': 'Premium gradnja',
    'luxury': 'Luksuzna gradnja',
    'lowcost': 'Ekonomična gradnja'
  };

  const locationNames = {
    'urban': 'Urbano područje',
    'suburban': 'Prigradsko područje',
    'rural': 'Ruralno područje'
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Gradnje Kuće",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte troškove gradnje kuće. Uključuje procjenu za različite vrste gradnje, dodatne elemente i lokacije.",
    "url": "https://online-kalkulator.com/kalkulator-gradnje-kuce",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun troškova gradnje",
      "Procjena vremena gradnje",
      "Različite vrste gradnje",
      "Dodatni elementi",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Gradnje Kuće | Izračun Troškova Gradnje"
        description="Izračunajte troškove gradnje kuće. Uključuje procjenu za različite vrste gradnje, dodatne elemente i lokacije."
        canonicalUrl="/kalkulator-gradnje-kuce"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Home className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Gradnje Kuće</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Basic Dimensions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Površina etaže (m²)
                  </label>
                  <input
                    type="number"
                    value={values.floorArea}
                    onChange={(e) => handleInputChange('floorArea', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 100"
                    min="10"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Broj etaža
                  </label>
                  <select
                    value={values.floors}
                    onChange={(e) => handleInputChange('floors', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="1">1 etaža</option>
                    <option value="2">2 etaže</option>
                    <option value="3">3 etaže</option>
                    <option value="4">4 etaže</option>
                  </select>
                </div>
              </div>

              {/* Construction Type and Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta gradnje
                  </label>
                  <select
                    value={values.constructionType}
                    onChange={(e) => handleInputChange('constructionType', e.target.value as ConstructionState['constructionType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="lowcost">Ekonomična gradnja</option>
                    <option value="standard">Standardna gradnja</option>
                    <option value="premium">Premium gradnja</option>
                    <option value="luxury">Luksuzna gradnja</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lokacija
                  </label>
                  <select
                    value={values.location}
                    onChange={(e) => handleInputChange('location', e.target.value as ConstructionState['location'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="urban">Urbano područje</option>
                    <option value="suburban">Prigradsko područje</option>
                    <option value="rural">Ruralno područje</option>
                  </select>
                </div>
              </div>

              {/* Additional Options */}
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeBasement}
                    onChange={(e) => handleInputChange('includeBasement', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi podrum</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeGarage}
                    onChange={(e) => handleInputChange('includeGarage', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi garažu</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeRoof}
                    onChange={(e) => handleInputChange('includeRoof', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi krov</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeInterior}
                    onChange={(e) => handleInputChange('includeInterior', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi unutarnje uređenje</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={values.includeUtilities}
                    onChange={(e) => handleInputChange('includeUtilities', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Uključi instalacije (struja, voda, grijanje)</span>
                </label>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Ukupna površina</div>
                      <div className="text-xl font-bold text-blue-600">{results.totalArea} m²</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg text-center">
                      <div className="text-gray-600 text-sm mb-1">Cijena po m²</div>
                      <div className="text-xl font-bold text-blue-600">{formatCurrency(results.costPerSquareMeter)}</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-medium text-gray-700">Troškovi po stavkama:</h3>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        Osnovna konstrukcija ({constructionTypeNames[values.constructionType]}):
                      </span>
                      <span className="font-medium">{formatCurrency(results.baseCost)}</span>
                    </div>
                    
                    {values.includeBasement && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Podrum:</span>
                        <span className="font-medium">{formatCurrency(results.basementCost)}</span>
                      </div>
                    )}
                    
                    {values.includeGarage && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Garaža:</span>
                        <span className="font-medium">{formatCurrency(results.garageCost)}</span>
                      </div>
                    )}
                    
                    {values.includeRoof && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Krov:</span>
                        <span className="font-medium">{formatCurrency(results.roofCost)}</span>
                      </div>
                    )}
                    
                    {values.includeInterior && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Unutarnje uređenje:</span>
                        <span className="font-medium">{formatCurrency(results.interiorCost)}</span>
                      </div>
                    )}
                    
                    {values.includeUtilities && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Instalacije:</span>
                        <span className="font-medium">{formatCurrency(results.utilitiesCost)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">
                        Faktor lokacije ({locationNames[values.location]}):
                      </span>
                      <span className="font-medium">×{results.locationFactor}</span>
                    </div>
                  </div>

                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupni trošak:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.totalCost)}
                    </span>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <div className="text-gray-600 text-sm mb-1">Procijenjeno vrijeme gradnje</div>
                    <div className="text-lg font-semibold text-blue-600">{results.estimatedTime} mjeseci</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Gradnje Kuće</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator gradnje kuće je koristan alat koji vam pomaže procijeniti troškove
                  izgradnje kuće prema vašim specifikacijama. Uzima u obzir veličinu kuće, vrstu
                  gradnje, lokaciju i dodatne elemente kako bi vam pružio okvirnu procjenu troškova
                  i vremena potrebnog za izgradnju.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Vrste Gradnje</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Ekonomična gradnja</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Osnovni materijali i standardna izvedba</li>
                      <li>Jednostavniji tlocrt i konstrukcija</li>
                      <li>Standardna izolacija</li>
                      <li>Osnovna oprema i instalacije</li>
                      <li>Cijena: oko 600 €/m²</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Standardna gradnja</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Kvalitetni materijali i dobra izvedba</li>
                      <li>Uobičajeni tlocrt i konstrukcija</li>
                      <li>Dobra toplinska i zvučna izolacija</li>
                      <li>Kvalitetna oprema i instalacije</li>
                      <li>Cijena: oko 800 €/m²</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Premium gradnja</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Visokokvalitetni materijali i izvedba</li>
                      <li>Složeniji tlocrt i konstrukcija</li>
                      <li>Napredna energetska učinkovitost</li>
                      <li>Premium oprema i instalacije</li>
                      <li>Cijena: oko 1200 €/m²</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Luksuzna gradnja</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Vrhunski materijali i izvedba</li>
                      <li>Složeni tlocrt i konstrukcija</li>
                      <li>Najviša razina energetske učinkovitosti</li>
                      <li>Luksuzna oprema i napredne instalacije</li>
                      <li>Cijena: oko 1800 €/m² i više</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faktori koji Utječu na Cijenu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Veličina i složenost kuće</li>
                  <li>Kvaliteta materijala i opreme</li>
                  <li>Lokacija gradnje</li>
                  <li>Pristupačnost terena</li>
                  <li>Vrsta tla i potreba za posebnim temeljima</li>
                  <li>Energetski standard (niskoenergijska, pasivna kuća)</li>
                  <li>Dodatni sadržaji (podrum, garaža, bazen)</li>
                  <li>Cijena rada u regiji</li>
                  <li>Sezona gradnje</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite površinu jedne etaže u kvadratnim metrima</li>
                  <li>Odaberite broj etaža</li>
                  <li>Odaberite vrstu gradnje prema vašim potrebama i budžetu</li>
                  <li>Odaberite lokaciju gradnje</li>
                  <li>Označite dodatne elemente koje želite uključiti</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Faze Gradnje Kuće</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Projektiranje i ishođenje dozvola (2-6 mjeseci)</li>
                  <li>Pripremni radovi i iskop (1-2 tjedna)</li>
                  <li>Temelji (2-4 tjedna)</li>
                  <li>Gruba gradnja (2-4 mjeseca)</li>
                  <li>Krov (2-4 tjedna)</li>
                  <li>Instalacije (struja, voda, grijanje) (1-2 mjeseca)</li>
                  <li>Unutarnji radovi (žbukanje, estrih) (1-2 mjeseca)</li>
                  <li>Završni radovi (podovi, keramika, bojanje) (1-3 mjeseca)</li>
                  <li>Uređenje okoliša (2-4 tjedna)</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračuni su informativne prirode</li>
                  <li>Stvarni troškovi mogu značajno varirati</li>
                  <li>Uvijek zatražite ponude od više izvođača</li>
                  <li>Predvidite dodatnih 10-15% za nepredviđene troškove</li>
                  <li>Konzultirajte arhitekta i građevinskog inženjera</li>
                  <li>Provjerite lokalne propise i potrebne dozvole</li>
                  <li>Razmislite o energetskoj učinkovitosti i dugoročnim uštedama</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Uštedu</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Planirajte jednostavan i učinkovit tlocrt</li>
                  <li>Izbjegavajte nepotrebne složene arhitektonske elemente</li>
                  <li>Usporedite cijene materijala od različitih dobavljača</li>
                  <li>Razmotrite mogućnost izvođenja nekih radova samostalno</li>
                  <li>Gradite u fazama ako je potrebno</li>
                  <li>Uložite u kvalitetnu izolaciju za dugoročne uštede</li>
                  <li>Odaberite energetski učinkovite sustave grijanja i hlađenja</li>
                  <li>Planirajte unaprijed kako biste izbjegli skupe izmjene tijekom gradnje</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorGradnjeKuce;